// The location a page shows data for, chosen with LocationLookup or the map marker, and kept in the URL as
// ?lat=&lng=&title= so it can be shared, reloaded, and stepped through with back/forward.

// selections whose name is a label from this app rather than a place name; these stay out of the URL and titles
const UNNAMED_MODULES = ["currentLocation", "savedLocation", "mapLocation"];

export function currentLocationSelection() {
  return { name: "Current Location", module: "currentLocation" };
}

// old links double-encoded the title (encodeURI before the router encoded it again), so decode once more;
// a title that is not valid URI encoding (e.g. "100%") is used as is
export function decodeLocationTitle(title) {
  if (typeof title !== "string" || !title) {
    return null;
  }
  try {
    return decodeURI(title);
  } catch {
    return title;
  }
}

function firstQueryValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

export function selectionFromQuery(query) {
  const latitude = parseFloat(firstQueryValue(query?.lat));
  const longitude = parseFloat(firstQueryValue(query?.lng));
  if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) {
    return null;
  }
  const title = decodeLocationTitle(firstQueryValue(query.title));
  return {
    name: title || "Saved Location",
    module: title ? "urlLocation" : "savedLocation",
    location: { latitude, longitude },
  };
}

// the lat/lng/title query for a selection; current location is not written to the URL
export function queryForSelection(selection) {
  const location = selection?.location;
  if (selection?.module === "currentLocation" || !isNumber(location?.latitude) || !isNumber(location?.longitude)) {
    return {};
  }
  const query = { lat: String(round(location.latitude, 5)), lng: String(round(location.longitude, 5)) };
  if (!UNNAMED_MODULES.includes(selection.module) && selection.name) {
    query.title = selection.name;
  }
  return query;
}

function isValidLatitude(value) {
  return isNumber(value) && value >= -90 && value <= 90;
}

function isValidLongitude(value) {
  return isNumber(value) && value >= -180 && value <= 180;
}

function locationQuery(query) {
  const result = {};
  for (const key of ["lat", "lng", "title"]) {
    const value = firstQueryValue(query?.[key]);
    if (value != null && value !== "") {
      result[key] = String(value);
    }
  }
  return result;
}

function sameCoordinate(a, b) {
  return a === b || round(parseFloat(a), 5) === round(parseFloat(b), 5);
}

function sameLocationQuery(a, b) {
  const left = locationQuery(a);
  const right = locationQuery(b);
  return (
    sameCoordinate(left.lat, right.lat) &&
    sameCoordinate(left.lng, right.lng) &&
    decodeLocationTitle(left.title) === decodeLocationTitle(right.title)
  );
}

// rejects if `promise` takes longer than `ms`
function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Timed out after ${ms} ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * @param geolocation the page's useLocation() instance, if it needs one for anything else (e.g. watching)
 */
export function useSelectedLocation(geolocation = useLocation()) {
  const route = useRoute();
  const router = useRouter();
  const { location, updateLocation, isLocationSupported, error } = geolocation;
  const hasGeolocation = import.meta.server || "geolocation" in navigator;

  // Without a location in the URL, try the current location, even after an earlier denial (like the home page): if
  // access is still blocked the browser fails at once, without a prompt, and locationError explains it.
  function initialSelection() {
    return selectionFromQuery(route.query) ?? (hasGeolocation ? currentLocationSelection() : null);
  }

  const selectedLocation = ref(initialSelection());

  // only changes when the coordinates do, so pages don't refetch when e.g. the web elevation lookup finishes
  const coordinates = computed((previous) => {
    const source =
      selectedLocation.value?.module === "currentLocation" ? location.value : selectedLocation.value?.location;
    if (!isNumber(source?.latitude) || !isNumber(source?.longitude)) {
      return null;
    }
    if (previous?.latitude === source.latitude && previous?.longitude === source.longitude) {
      return previous;
    }
    return { latitude: source.latitude, longitude: source.longitude };
  });

  // the selected place's name, for headings and page titles
  const placeName = computed(() => {
    const selection = selectedLocation.value;
    return selection && !UNNAMED_MODULES.includes(selection.module) ? selection.name || null : null;
  });

  function setMapLocation({ lat, lng }) {
    selectedLocation.value = {
      name: "Map Location",
      module: "mapLocation",
      location: { latitude: lat, longitude: lng },
    };
  }

  async function locate() {
    await updateLocation();
    if (!isLocationSupported.value && selectedLocation.value?.module === "currentLocation") {
      // denied: clear the selection so the search box is ready, and locationError explains why
      selectedLocation.value = null;
    }
  }

  // why the current location couldn't be found, while the page has nothing else to show (show LocationProblem)
  const locationError = computed(() => {
    const selection = selectedLocation.value;
    if (selection && selection.module !== "currentLocation") {
      return null;
    }
    return isGeolocationError(error) ? error.value : null;
  });

  watch(selectedLocation, (selection) => {
    if (selection == null) {
      return;
    }
    if (selection.module === "currentLocation") {
      locate();
    }
    const query = queryForSelection(selection);
    if (!sameLocationQuery(route.query, query)) {
      const otherParams = Object.fromEntries(
        Object.entries(route.query).filter(([key]) => !["lat", "lng", "title"].includes(key)),
      );
      router.push({ query: { ...otherParams, ...query } });
    }
  });

  // back/forward only changes the query, which keeps the same page component mounted
  watch(
    () => locationQuery(route.query),
    (query) => {
      if (!sameLocationQuery(query, queryForSelection(selectedLocation.value))) {
        selectedLocation.value = initialSelection();
      }
    },
  );

  // the user allowed location access again from LocationPermissionButton
  onLocationEnabled(() => {
    if (!selectedLocation.value || selectedLocation.value.module === "currentLocation") {
      selectedLocation.value = currentLocationSelection();
    }
  });

  // after hydration, so the position (and the web elevation lookup it starts) doesn't arrive mid-hydration
  onMounted(() => {
    if (selectedLocation.value?.module === "currentLocation") {
      locate();
    }
  });

  // the initial location and its data, when the page loaded them in setup with useInitialLocationData
  let initialCoordinates = null;
  let initialData = null;

  /**
   * Loads `fetch(coordinates)` for a location in the URL during setup, with useAsyncData: on the server, so the
   * data is in the HTML, and when hydrating, from the payload instead of fetching again. Await the result in
   * setup. Returns null when there is nothing to load this way: for the current location, which only the browser
   * knows, and after client-side navigation, where the page shows its placeholders instead of holding up the
   * navigation. onCoordinatesChange then skips the initial location, unless this failed and the browser should
   * try again.
   *
   * `fetch` runs inside useAsyncData, so it should use $fetch, not useFetch. On the server it gets `timeout` ms,
   * so a slow lookup leaves its data to the browser instead of delaying the page.
   */
  function useInitialLocationData(key, fetch, { timeout = 3000 } = {}) {
    const value = coordinates.value;
    if (!value || selectedLocation.value?.module === "currentLocation") {
      return null;
    }
    if (import.meta.client && !useNuxtApp().isHydrating) {
      return null;
    }
    initialCoordinates = value;
    initialData = useAsyncData(`${key}:${value.latitude},${value.longitude}`, () =>
      import.meta.server ? withTimeout(fetch(value), timeout) : fetch(value),
    );
    return initialData;
  }

  // Calls `load(coordinates)` for the initial location (unless useInitialLocationData loaded it) and whenever it
  // moves, starting once the page is mounted. Except for useInitialLocationData, data loads in the browser only:
  // on the server, fetches after an await run without the Nuxt context and the render does not wait for them.
  // Waiting for mount also keeps data that arrives during hydration from rendering content the server didn't.
  //
  // `load` runs from a watcher, outside the component instance, like a click handler: called during mount, a
  // lazy useFetch would queue its request for a beforeMount that has already happened, and never send it.
  const mounted = ref(false);
  onMounted(() => {
    mounted.value = true;
  });
  function onCoordinatesChange(load) {
    watch([coordinates, mounted], ([value, isMounted]) => {
      if (!value || !isMounted) {
        return;
      }
      const loaded = value === initialCoordinates && initialData.data.value != null && !initialData.error.value;
      if (!loaded) {
        load(value);
      }
    });
  }

  return {
    selectedLocation,
    coordinates,
    placeName,
    locationError,
    setMapLocation,
    useInitialLocationData,
    onCoordinatesChange,
    geolocation,
  };
}
